import { useDispatch, useSelector } from 'react-redux';
import { Country, State, UserType } from '../../../types/generated';
import { gql } from '@apollo/client';
import validations from '../../../lib/helpers/validations';
import { useToast } from '../../../hooks/useToast';
import { login as reduxLogin } from '../../../redux/actions';
import { useState, useRef, useCallback, useEffect } from 'react';
import {
	FlattenedUser,
	UserRegistration,
	getAuthedUserFlattenedUserFieldsFragment,
} from '../../../types/user';
import { AddressFieldsProps } from '../../../mui/AddressFieldSet';
import {
	useClientOnboardingYourInfo_CreateClientUserMutation,
	useClientOnboardingYourInfo_UpdateClientUserMutation,
	useClientOnboardingYourInfo_LoginLazyQuery,
	ClientOnboardingYourInfoClientUserFragment,
	ClientOnboardingYourInfoAddressClientUserFragment,
	useClientOnboardingYourInfo_GetClientUserQuery,
	useClientOnboardingYourInfo_GetAuthedUserLazyQuery,
} from '#types';
import {
	NonNullNoTypename,
	getEnumKeyByValue,
} from '../../../lib/helpers/apollo';

const clientOnboardingYourInfoAddressClientUserFragment = gql`
	fragment clientOnboardingYourInfoAddressClientUser on ClientUser {
		addressLine1
		addressLine2
		city
		postalCode
		state
		country
	}
`;

const clientOnboardingYourInfoClientUserFragment = gql`
	fragment clientOnboardingYourInfoClientUser on ClientUser {
		id
		firstNameOne
		lastNameOne
		firstNameTwo
		lastNameTwo
		...clientOnboardingYourInfoAddressClientUser
		user {
			id
			email
			userType
			acceptedTerms
			consentedToAgeAndMarketingEmails
		}
	}
	${ clientOnboardingYourInfoAddressClientUserFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getClientUserWhereQuery = gql`
	query clientOnboardingYourInfo_getClientUser(
		$where: ClientUserWhereUniqueInput!
	) {
		getClientUserWhere(where: $where) {
			...clientOnboardingYourInfoClientUser
		}
	}
	${ clientOnboardingYourInfoClientUserFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createClientUserMutation = gql`
	mutation clientOnboardingYourInfo_createClientUser(
		$data: ClientUserCreateInput!
	) {
		createClientUser(data: $data) {
			...clientOnboardingYourInfoClientUser
		}
	}
	${ clientOnboardingYourInfoClientUserFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const updateClientUserMutation = gql`
	mutation clientOnboardingYourInfo_updateClientUser(
		$data: ClientUserUpdateCustomInput!
		$where: ClientUserWhereUniqueInput!
	) {
		updateClientUser(data: $data, where: $where) {
			...clientOnboardingYourInfoClientUser
		}
	}
	${ clientOnboardingYourInfoClientUserFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const loginQuery = gql`
	query clientOnboardingYourInfo_login($data: UserLoginInput!) {
		login(data: $data) {
			token
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getAuthedUserQuery = gql`
	query ClientOnboardingYourInfo_getAuthedUser($where: UserWhereUniqueInput!) {
		getAuthedUser(where: $where) {
			...getAuthedUserFlattenedUserFields
		}
	}
	${ getAuthedUserFlattenedUserFieldsFragment }
`;

export const useYourInfo = () => {
	const { showError } = useToast();
	const user: FlattenedUser = useSelector( ( state: any ) => state?.user );
	const userRegistration: UserRegistration = useSelector(
		( state: any ) => state?.userRegistration
	);
	const loggedInClientUser =
		user?.isLoggedIn && user.userType === UserType.ClientUser
			? user
			: undefined;
	const dispatch = useDispatch();
	const [ firstNameOne, setFirstNameOne ] = useState(
		loggedInClientUser?.firstNameOne
			? loggedInClientUser.firstNameOne
			: undefined
	);
	const [ lastNameOne, setLastNameOne ] = useState(
		loggedInClientUser?.lastNameOne ? loggedInClientUser.lastNameOne : undefined
	);
	const [ firstNameTwo, setFirstNameTwo ] = useState(
		loggedInClientUser?.firstNameTwo
			? loggedInClientUser.firstNameTwo
			: undefined
	);
	const [ lastNameTwo, setLastNameTwo ] = useState(
		loggedInClientUser?.lastNameTwo ? loggedInClientUser.lastNameTwo : undefined
	);
	const [ address, setAddress ] = useState<
	/* using the presentation component props here rather than Apollo type, because we
	need to do the "massaging" of data into that shape in this hook */
	AddressFieldsProps['address'] | undefined
	>( {
		addressLine1: loggedInClientUser
			? loggedInClientUser.addressLine1
			: undefined,
		addressLine2: loggedInClientUser
			? loggedInClientUser.addressLine2
			: undefined,
		city: loggedInClientUser ? loggedInClientUser.city : undefined,
		state: loggedInClientUser ? loggedInClientUser.state : undefined,
		country: loggedInClientUser ? loggedInClientUser.country : undefined,
		postalCode: loggedInClientUser ? loggedInClientUser.postalCode : undefined,
	} );
	const [ firstNameOneValidation, setFirstNameOneValidation ] = useState<
	string | undefined
	>();
	const [ lastNameOneValidation, setLastNameOneValidation ] = useState<
	string | undefined
	>();
	const [ firstNameTwoValidation, setFirstNameTwoValidation ] = useState<
	string | undefined
	>();
	const [ lastNameTwoValidation, setLastNameTwoValidation ] = useState<
	string | undefined
	>();
	const [ addressValidation, setAddressValidation ] = useState<
	AddressFieldsProps['validate'] | undefined
	>();
	const [ termsOfServiceAccepted, setTermsOfServiceAccepted ] =
		useState<boolean>( false );
	const [ consentedToAgeAndMarketingEmails, setConsentedToAgeAndMarketingEmails, ] = useState<boolean>( false );
	const acceptanceOfTermsOfServiceProcessedRef = useRef( false );
	const [ termsOfServiceAcceptedValidation, setTermsOfServiceAcceptedValidation, ] = useState<string | undefined>();
	const [ consentedToAgeAndMarketingEmailsValidation, setConsentedToAgeAndMarketingEmailsValidation, ] = useState<string | undefined>();
	const onBlurValidationEnabledRef = useRef( false );
	const [ createClientUser ] =
		useClientOnboardingYourInfo_CreateClientUserMutation();
	const [ updateClientUser ] =
		useClientOnboardingYourInfo_UpdateClientUserMutation();
	const [ login ] = useClientOnboardingYourInfo_LoginLazyQuery();
	const [ getAuthedUser ] = useClientOnboardingYourInfo_GetAuthedUserLazyQuery();
	const { data: acceptedTermsData, error: acceptedTermsError } =
		useClientOnboardingYourInfo_GetClientUserQuery( {
			variables: {
				where: {
					id: loggedInClientUser?.id || '',
				},
			},
			skip: !loggedInClientUser?.id,
		} );
	useEffect( () => {
		if ( acceptedTermsError ) {
			showError( acceptedTermsError );
		} else if ( acceptedTermsData?.getClientUserWhere?.user ) {
			// since they've already accepted the terms, update state to indicate that
			setTermsOfServiceAccepted(
				!!acceptedTermsData.getClientUserWhere?.user?.acceptedTerms
			);
			setConsentedToAgeAndMarketingEmails(
				!!acceptedTermsData.getClientUserWhere?.user
					?.consentedToAgeAndMarketingEmails
			);
			acceptanceOfTermsOfServiceProcessedRef.current = true;
		}
	}, [
		acceptedTermsError,
		acceptedTermsData,
		showError
	] );
	useEffect( () => {
		if ( loggedInClientUser && acceptedTermsData ) {
			setTermsOfServiceAccepted(
				!!acceptedTermsData.getClientUserWhere?.user?.acceptedTerms
			);
			setConsentedToAgeAndMarketingEmails(
				!!acceptedTermsData.getClientUserWhere?.user
					?.consentedToAgeAndMarketingEmails
			);
			setFirstNameOne( acceptedTermsData.getClientUserWhere?.firstNameOne );
			if ( acceptedTermsData.getClientUserWhere?.lastNameOne ) {
				setLastNameOne( acceptedTermsData.getClientUserWhere?.lastNameOne );
			}
			if ( acceptedTermsData.getClientUserWhere?.firstNameTwo ) {
				setFirstNameTwo( acceptedTermsData.getClientUserWhere?.firstNameTwo );
			}
			if ( acceptedTermsData.getClientUserWhere?.lastNameTwo ) {
				setLastNameTwo( acceptedTermsData.getClientUserWhere?.lastNameTwo );
			}
			setAddress( {
				addressLine1: acceptedTermsData.getClientUserWhere?.addressLine1 || '',
				addressLine2: acceptedTermsData.getClientUserWhere?.addressLine2 || '',
				city: acceptedTermsData.getClientUserWhere?.city || '',
				state: acceptedTermsData.getClientUserWhere?.state,
				country: acceptedTermsData.getClientUserWhere?.country,
				postalCode: acceptedTermsData.getClientUserWhere?.postalCode || '',
			} );
		}
	}, [
		loggedInClientUser,
		acceptedTermsData,
		setFirstNameOne,
		setLastNameOne,
		setFirstNameTwo,
		setLastNameTwo,
		setAddress,
		setTermsOfServiceAccepted,
	] );

	const onValidate = useCallback(
		( force = false ) => {
			if ( !force && !onBlurValidationEnabledRef.current ) {
				return true;
			}
			onBlurValidationEnabledRef.current = true;
			const clientNameErrors = validations.validateClientNames( {
				firstNameOne,
				lastNameOne,
				firstNameTwo,
				lastNameTwo,
			} );
			const untranslatedAddressValidation = validations.validateAddress(
				{
					line1: address?.addressLine1,
					line2: address?.addressLine2,
					city: address?.city,
					state: address?.state,
					postalCode: address?.postalCode,
					country: address?.country,
				},
				undefined,
				true
			);
			const namesHaveValidationErrors =
				clientNameErrors && Object.keys( clientNameErrors ).length > 0;
			const addressHasValidationErrors =
				untranslatedAddressValidation &&
				Object.keys( untranslatedAddressValidation ).length > 0;
			const termsOfServiceAcceptedErrors = !termsOfServiceAccepted
				? 'Almost there! Please agree to both RPC\'s Terms & Conditions and Stripe\'s Terms & Conditions.'
				: undefined;
			const consentedToAgeAndMarketingEmailsErrors =
				!consentedToAgeAndMarketingEmails
					? 'Oops, please accept all terms in order to proceed'
					: undefined;
			setFirstNameOneValidation( clientNameErrors.firstNameOne );
			setLastNameOneValidation( clientNameErrors.lastNameOne );
			setFirstNameTwoValidation( clientNameErrors.firstNameTwo );
			setLastNameTwoValidation( clientNameErrors.lastNameTwo );
			setAddressValidation( {
				addressLine1: untranslatedAddressValidation?.line1,
				addressLine2: untranslatedAddressValidation?.line2,
				city: untranslatedAddressValidation?.city,
				state: untranslatedAddressValidation?.state,
				postalCode: untranslatedAddressValidation?.postalCode,
				country: untranslatedAddressValidation?.country,
			} );
			setTermsOfServiceAcceptedValidation( termsOfServiceAcceptedErrors );
			setConsentedToAgeAndMarketingEmailsValidation(
				consentedToAgeAndMarketingEmailsErrors
			);
			if (
				namesHaveValidationErrors ||
				addressHasValidationErrors ||
				termsOfServiceAcceptedErrors ||
				consentedToAgeAndMarketingEmailsErrors
			) {
				return false;
			}
			return true;
		},
		[
			firstNameOne,
			lastNameOne,
			firstNameTwo,
			lastNameTwo,
			termsOfServiceAccepted,
			address?.addressLine1,
			address?.addressLine2,
			address?.city,
			address?.state,
			address?.postalCode,
			address?.country,
			consentedToAgeAndMarketingEmails,
		]
	);
	const onBack = useCallback( () => {
		onBlurValidationEnabledRef.current = false;
	}, [] );
	const onNext = useCallback( async () => {
		onBlurValidationEnabledRef.current = true;
		const validationResult = onValidate( true );
		if ( !validationResult ) {
			return false;
		}

		// A helper just so we do the same thing after a create and an update
		const handleSuccessfulAction = async (
			user: NonNullable<ClientOnboardingYourInfoClientUserFragment>
		) => {
			return true;
		};
		const state = address?.state
			? ( getEnumKeyByValue( State, address.state ) as State )
			: undefined;
		const country = address?.country
			? ( getEnumKeyByValue( Country, address.country ) as Country )
			: undefined;

		/* ensure the types of these values below here are never undefined - they should always be
	defined or validation has failed (or possibly the enum values don't match the <select> values
	is possible too...) */
		if ( !state || !country || !address?.addressLine1 ) {
			return false;
		}
		const presentationAddressToPrismaAddress:
		| NonNullNoTypename<ClientOnboardingYourInfoAddressClientUserFragment>
		| undefined =
			address?.addressLine1 &&
			address?.city &&
			address?.state &&
			address?.postalCode &&
			address?.country
				? {
					addressLine1: address.addressLine1,
					addressLine2: address.addressLine2 || '',
					city: address.city,
					state,
					postalCode: address.postalCode,
					country,
				  }
				: undefined;
		// Do either the create or the update
		if ( loggedInClientUser ) {
			const response = await updateClientUser( {
				variables: {
					data: {
						firstNameOne,
						lastNameOne,
						firstNameTwo,
						lastNameTwo,
						...presentationAddressToPrismaAddress,
						acceptedTerms: new Date(),
						consentedToAgeAndMarketingEmails: new Date(),
					},
					where: {
						id: loggedInClientUser.id,
					},
				},
			} );
			if ( response.errors || !response.data?.updateClientUser ) {
				response.errors?.forEach( ( error ) => showError( error ) );
				return false;
			} else {
				return await handleSuccessfulAction( response.data.updateClientUser );
			}
		} else {
			const email = userRegistration
				? userRegistration.coreUserFields?.email
				: undefined;
			const password = userRegistration
				? userRegistration.coreUserFields?.password
				: undefined;
			if (
				!email ||
				!password ||
				!firstNameOne ||
				!lastNameOne ||
				!presentationAddressToPrismaAddress
			) {
				return false;
			}
			const response = await createClientUser( {
				variables: {
					data: {
						email,
						password,
						firstNameOne,
						lastNameOne,
						firstNameTwo,
						lastNameTwo,
						...presentationAddressToPrismaAddress,
						...( userRegistration?.verificationToken?.id
							? { verificationTokenID: userRegistration.verificationToken.id }
							: undefined ),
						...( userRegistration?.invitation?.id
							? { invitationID: userRegistration.invitation.id }
							: undefined ),
						acceptedTerms: new Date(),
						consentedToAgeAndMarketingEmails: new Date(),
					},
				},
			} );
			if ( response.errors || !response.data?.createClientUser ) {
				response.errors?.forEach( ( error ) => showError( error ) );
				return false;
			} else {
				const loginResponse = await login( {
					variables: {
						data: {
							email,
							password,
						},
					},
				} );
				if ( loginResponse.error ) {
					showError( loginResponse.error );
					return false;
				} else if ( loginResponse.data?.login ) {
					dispatch(
						reduxLogin(
							response.data?.createClientUser,
							loginResponse.data.login.token
						)
					);
					return await handleSuccessfulAction( response.data.createClientUser );
				} else {
					return false;
				}
			}
		}
	}, [
		createClientUser,
		dispatch,
		firstNameOne,
		lastNameOne,
		firstNameTwo,
		lastNameTwo,
		address,
		loggedInClientUser,
		login,
		onValidate,
		updateClientUser,
		userRegistration,
		showError,
	] );

	useEffect( () => {
		const loginIfPossible = async () => {
			if ( userRegistration?.coreUserFields ) {
				const result = await login( {
					variables: {
						data: {
							email: userRegistration?.coreUserFields?.email || '',
							password: userRegistration?.coreUserFields?.password || '',
						},
					},
				} );
				if ( result.data?.login?.token ) {
					const response = await getAuthedUser( {
						variables: {
							where: {
								email:
									userRegistration?.coreUserFields?.email ||
									loggedInClientUser?.user.email,
							},
						},
					} );
					if ( response.data?.getAuthedUser ) {
						const unflattenedUser = response.data?.getAuthedUser;
						const partiallyFlattenedUser = {
							...unflattenedUser.clientUser,
							groups: unflattenedUser?.groups,
						};
						dispatch(
							reduxLogin( partiallyFlattenedUser, result.data.login.token )
						);
					}
				}
			}
		};
		loginIfPossible();
	}, [
		login,
		userRegistration?.coreUserFields,
		dispatch,
		getAuthedUser,
		loggedInClientUser?.user?.email,
	] );
	return {
		firstNameOne,
		setFirstNameOne,
		firstNameOneValidation,
		lastNameOne,
		setLastNameOne,
		lastNameOneValidation,
		firstNameTwo,
		setFirstNameTwo,
		firstNameTwoValidation,
		lastNameTwo,
		setLastNameTwo,
		lastNameTwoValidation,
		address,
		setAddress,
		addressValidation,
		onValidate,
		onBack,
		termsOfServiceAccepted,
		setTermsOfServiceAccepted,
		termsOfServiceAcceptedValidation,
		consentedToAgeAndMarketingEmails,
		setConsentedToAgeAndMarketingEmails,
		consentedToAgeAndMarketingEmailsValidation,
		onNext,
	};
};
